import React, { useState, useEffect } from 'react';
import {
    Box,
    Avatar,
    Typography,
    IconButton,
    Paper,
    Stack,
    Chip,
} from '@mui/material';
import {
    Call as CallIcon,
    CallEnd as CallEndIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    VolumeUp as VolumeUpIcon,
    VolumeDown as VolumeDownIcon,
} from '@mui/icons-material';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
    setCreateCallOffer,
    setAnswerCall,
    setCloseCall,
    sendIceCandidate,
    setEndCall,
    setAcknowledgement,
} from 'App/store/actions/call.action';

interface DataCallProps {
    call: any;
    setCreateCallOffer: (offer: any) => void;
    setAnswerCall: (answer: any) => void;
    setCloseCall: () => void;
    sendIceCandidate: (candidate: any) => void;
    callOffer: any;
    callType: any;
    callAnswer: any;
    callStatus: string;
    callAnswerReceived: any;
    callActive: any;
    candidate: any;
    receiveIceCandidate: any;
    setEndCall: () => void;
    user: any;
    windowToggle: (show: boolean) => void;
    receiveAckMsg: string;
    setAcknowledgement: () => void;
}

const configuration = {
    iceServers: [
        { urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302'] },
        {
            urls: "turn:13.41.233.187:3478",
            username: "licen",
            credential: "LicenPass",
        }
    ],
};

let pc: RTCPeerConnection | null = null;
let pendingCandidates: RTCIceCandidate[] = [];
let retryTimeout: NodeJS.Timeout | null = null;
let retryAttempts = 0;

const DataCall: React.FC<DataCallProps> = ({
    call,
    setCreateCallOffer,
    setAnswerCall,
    setCloseCall,
    sendIceCandidate,
    callOffer,
    callType,
    callAnswer,
    callStatus,
    callAnswerReceived,
    callActive,
    candidate,
    receiveIceCandidate,
    setEndCall,
    user,
    windowToggle,
    receiveAckMsg,
    setAcknowledgement,
}) => {
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
    const [isCalling, setIsCalling] = useState<boolean>(false);
    const [mute, setMute] = useState<boolean>(false);
    const [speaker, setSpeaker] = useState<boolean>(false);
    const [callDuration, setCallDuration] = useState<number>(0);

    // Timer for call duration
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (callActive === 'started') {
            interval = setInterval(() => {
                setCallDuration(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [callActive]);

    // Format call duration
    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (!call) {
            setLocalStream(null);
            setRemoteStream(null);
            setCallDuration(0);
            windowToggle(false);
        } else {
            if (!localStream) {
                windowToggle(true);
                startLocalStream();
            }
        }
        retryTimeout = null;
        retryAttempts = 0;

        return () => {
            setLocalStream(null);
            setRemoteStream(null);
            setCallDuration(0);
            windowToggle(false);
        };
    }, [call]);

    useEffect(() => {
        const getPermission = async () => {
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                console.log('Audio permission granted');
            } catch (err) {
                console.warn('Permission denied:', err);
            }
        };

        getPermission();
    }, []);

    const startLocalStream = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false,
            });
            setLocalStream(stream);
        } catch (error) {
            console.error('Error accessing local stream:', error);
        }
    };

    useEffect(() => {
        if (callAnswerReceived) {
            handleAnswerReceived(callAnswerReceived);
        }
    }, [callAnswerReceived]);

    useEffect(() => {
        if (receiveIceCandidate) {
            receiveICECandidateConfirm(receiveIceCandidate);
        }
    }, [receiveIceCandidate]);

    const createPeerConnection = async () => {
        pc = new RTCPeerConnection(configuration);

        if (localStream) {
            localStream.getTracks().forEach((track) => {
                pc!.addTrack(track, localStream);
            });
        }

        pc.ontrack = (event) => {
            setRemoteStream(event.streams[0]);
        };

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                sendIceCandidate(event.candidate);
            } else {
                console.log('ICE gathering complete');
            }
        };

        pc.oniceconnectionstatechange = async () => {
            console.log('ICE connection state:', pc!.iceConnectionState);

            if (pc!.iceConnectionState === 'disconnected' || pc!.iceConnectionState === 'failed') {
                console.warn('ICE connection state disconnected or failed. Attempting reconnection...');
                retryAttempts = 0;
                attemptReconnection();
            } else if (pc!.iceConnectionState === 'connected') {
                console.log('ICE connection restored.');
                if (retryTimeout) {
                    clearTimeout(retryTimeout);
                    retryTimeout = null;
                }
            }
        };
    };

    const attemptReconnection = async () => {
        if (retryAttempts >= 10) {
            console.error('Failed to reconnect after 10 attempts. Ending call.');
            closeCall();
            return;
        }

        try {
            retryAttempts += 1;
            console.log(`Attempting ICE restart... (Attempt ${retryAttempts})`);
            await pc!.restartIce();

            retryTimeout = setTimeout(() => {
                console.warn('Reconnection attempt timed out. Retrying...');
                attemptReconnection();
            }, 1000);
        } catch (error) {
            console.error('ICE restart failed:', error);
            if (retryAttempts >= 10) {
                console.error('Max retries reached. Ending call.');
                closeCall();
            }
        }
    };

    const handleCall = async () => {
        await createPeerConnection();
        const offer = await pc!.createOffer();
        await pc!.setLocalDescription(offer);
        setCreateCallOffer(offer);
    };

    const handleReceiveCall = async () => {
        try {
            await createPeerConnection();
            await pc!.setRemoteDescription(new RTCSessionDescription(callOffer));
            const answer = await pc!.createAnswer();
            await pc!.setLocalDescription(answer);
            setAnswerCall(answer);
            processPendingCandidates();
        } catch (error) {
            console.error('Error setting remote description or creating answer:', error);
        }
    };

    const handleAnswerReceived = async (answer: any) => {
        try {
            if (pc && pc.signalingState === 'have-local-offer') {
                await pc.setRemoteDescription(new RTCSessionDescription(answer));
                console.log('Answer received and set successfully');
                processPendingCandidates();
            } else {
                console.error('Peer connection is not in a state to set remote description');
            }
        } catch (error) {
            console.error('Error setting remote description:', error);
        }
    };

    const processPendingCandidates = async () => {
        for (const candidate of pendingCandidates) {
            try {
                await pc!.addIceCandidate(candidate);
            } catch (error) {
                console.error('Error adding ICE candidate:', error);
            }
        }
        pendingCandidates = [];
    };

    const receiveICECandidateConfirm = async (candidate: any) => {
        if (pc) {
            if (pc.remoteDescription && pc.remoteDescription.type) {
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate));
                    console.log('ICE candidate successfully added', user.type);
                } catch (error) {
                    console.error('Error adding ICE candidate:', error);
                }
            } else {
                pendingCandidates.push(new RTCIceCandidate(candidate));
            }
        }
    };

    useEffect(() => {
        if (!isCalling && callActive && callActive === 'pending') {
            setAcknowledgement();
        }
    }, [isCalling, callActive]);

    useEffect(() => {
        if (callStatus === 'initiate' && localStream) {
            handleCall();
            setIsCalling(true);
        }
    }, [callStatus, localStream]);

    useEffect(() => {
        if (callStatus && callStatus === 'waiting') {
            setIsCalling(false);
        }
    }, [callStatus]);

    const toggleMute = () => {
        if (localStream && localStream.getAudioTracks().length > 0) {
            localStream.getAudioTracks().forEach((track) => {
                track.enabled = !mute;
            });
            setMute(!mute);
        }
    };

    const toggleSpeaker = () => {
        setSpeaker(!speaker);
        // Note: Web audio routing is limited compared to mobile
        // You might need additional logic for speaker/earpiece switching
    };

    const endCall = () => {
        closeCall();
    };

    const cancelCall = () => {
        closeCall();
    };

    const closeCall = () => {
        try {
            if (pc) {
                pc.close();
                pc = null;
            }

            if (localStream) {
                localStream.getTracks().forEach((track) => {
                    track.stop();
                });
                setLocalStream(null);
            }

            setRemoteStream(null);
            setCallDuration(0);
            setCloseCall();
        } catch (error) {
            console.error('Error ending the call:', error);
        }
    };

    if (!call) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor: '#f5f5f5',
                p: 3,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    maxWidth: 400,
                    width: '100%',
                }}
            >
                <Avatar
                    src={isCalling ? call.to.img : call.from.img}
                    sx={{
                        width: 120,
                        height: 120,
                        mx: 'auto',
                        mb: 2,
                    }}
                />

                {callActive && callActive === 'pending' && (
                    <>
                        <Typography variant="h6" color="text.secondary" mb={1}>
                            {isCalling
                                ? `Calling ${call.to.name}...`
                                : `Incoming call from ${call.from.name}...`}
                        </Typography>
                        {isCalling && receiveAckMsg && (
                            <Chip label={receiveAckMsg} size="small" />
                        )}
                    </>
                )}

                {callActive && callActive === 'started' && (
                    <Typography variant="h6" color="text.secondary" mb={2}>
                        {formatDuration(callDuration)}
                    </Typography>
                )}

                {/* Incoming call buttons */}
                {!isCalling && callActive && callActive === 'pending' && (
                    <Stack direction="row" spacing={3} justifyContent="center" mt={3}>
                        <IconButton
                            onClick={handleReceiveCall}
                            sx={{
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                width: 60,
                                height: 60,
                                '&:hover': {
                                    backgroundColor: '#45a049',
                                },
                            }}
                        >
                            <CallIcon />
                        </IconButton>
                        <IconButton
                            onClick={cancelCall}
                            sx={{
                                backgroundColor: '#F44336',
                                color: 'white',
                                width: 60,
                                height: 60,
                                '&:hover': {
                                    backgroundColor: '#da190b',
                                },
                            }}
                        >
                            <CallEndIcon />
                        </IconButton>
                    </Stack>
                )}

                {/* Outgoing call cancel button */}
                {isCalling && callActive && callActive === 'pending' && (
                    <IconButton
                        onClick={cancelCall}
                        sx={{
                            backgroundColor: '#F44336',
                            color: 'white',
                            width: 60,
                            height: 60,
                            mt: 2,
                            '&:hover': {
                                backgroundColor: '#da190b',
                            },
                        }}
                    >
                        <CallEndIcon />
                    </IconButton>
                )}

                {/* Active call controls */}
                {callActive && callActive === 'started' && (
                    <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                        <IconButton
                            onClick={toggleMute}
                            sx={{
                                backgroundColor: mute ? '#F44336' : '#42a5f5',
                                color: 'white',
                                width: 50,
                                height: 50,
                                '&:hover': {
                                    backgroundColor: mute ? '#da190b' : '#1976d2',
                                },
                            }}
                        >
                            {mute ? <MicOffIcon /> : <MicIcon />}
                        </IconButton>
                        <IconButton
                            onClick={toggleSpeaker}
                            sx={{
                                backgroundColor: speaker ? '#FF9800' : '#42a5f5',
                                color: 'white',
                                width: 50,
                                height: 50,
                                '&:hover': {
                                    backgroundColor: speaker ? '#f57c00' : '#1976d2',
                                },
                            }}
                        >
                            {speaker ? <VolumeUpIcon /> : <VolumeDownIcon />}
                        </IconButton>
                        <IconButton
                            onClick={endCall}
                            sx={{
                                backgroundColor: '#F44336',
                                color: 'white',
                                width: 50,
                                height: 50,
                                '&:hover': {
                                    backgroundColor: '#da190b',
                                },
                            }}
                        >
                            <CallEndIcon />
                        </IconButton>
                    </Stack>
                )}
            </Paper>

            {/* Hidden audio elements for remote stream */}
            {remoteStream && (
                <audio
                    ref={(audio) => {
                        if (audio && remoteStream) {
                            audio.srcObject = remoteStream;
                        }
                    }}
                    autoPlay
                    style={{ display: 'none' }}
                />
            )}
        </Box>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
    setCreateCallOffer: (data: any) => dispatch(setCreateCallOffer(data)),
    setAnswerCall: (data: any) => dispatch(setAnswerCall(data)),
    sendIceCandidate: (data: any) => dispatch(sendIceCandidate(data)),
    setAcknowledgement: () => dispatch(setAcknowledgement()),
    setCloseCall: () => dispatch(setCloseCall()),
    setEndCall: () => dispatch(setEndCall()),
});

const mapStateToProps = (state: any) => ({
    callActive: state.call.callActive,
    callStatus: state.call.callStatus,
    callType: state.call.callType,
    callOffer: state.call.callOffer,
    callAnswer: state.call.callAnswer,
    receiveAckMsg: state.call.receiveAckMsg,
    callAnswerReceived: state.call.callAnswerReceived,
    candidate: state.call.candidate,
    receiveIceCandidate: state.call.receiveIceCandidate,
    call: state.call.call,
    user: state.user.user,
});

export default connect(mapStateToProps, mapDispatchToProps)(DataCall);